import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { Accumulating: "#2BD97C", Igniting: "#E3A44A", Mixed: "#8B93A3", Distributing: "#C75E5E" };
const CURVE_X = { Accumulating: 36, Igniting: 88, Mixed: 120, Distributing: 156 };

export default function LiveThemeCurve() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/public/themes/curve`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => d.themes?.length >= 4 && setData(d))
      .catch(() => {});
  }, []);

  if (!data) return null;
  const SEGMENTS = { Accumulating: [12, 66], Igniting: [72, 114], Mixed: [118, 134], Distributing: [140, 184] };
  const groups = {};
  data.themes.forEach((t) => {
    const key = SEGMENTS[t.lifecycle] ? t.lifecycle : "Mixed";
    (groups[key] = groups[key] || []).push(t);
  });
  const curveY = (x) => 52 - 40 * Math.exp(-((x - 100) ** 2) / 1800);
  const pts = [];
  Object.entries(groups).forEach(([key, ts]) => {
    const [s, e] = SEGMENTS[key];
    ts.forEach((t, i) => {
      const cx = s + ((i + 0.5) * (e - s)) / ts.length;
      pts.push({ t, cx, cy: curveY(cx), half: t.name.length * 0.98 });
    });
  });
  pts.sort((a, b) => a.cx - b.cx);
  const upLanes = [{ y: 6, end: -999 }, { y: 11, end: -999 }];
  const downLanes = [{ y: 64, end: -999 }, { y: 70, end: -999 }, { y: 76, end: -999 }, { y: 82, end: -999 }];

  return (
    <section id="theme-map" className="border-b border-[rgba(255,255,255,0.06)]">
      <div className="fp-section">
        <p className="fp-eyebrow mb-6">The Theme Map, live</p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
          These are the trades the specialists are building right now.
        </h2>
        <p className="mt-5 max-w-2xl text-base text-[#8B93A3] leading-relaxed">
          Every dot is a live basket of stocks backed by several of the world's best-performing
          funds at the same time. The map shows where the money sits: early on the left, crowded
          and leaving on the right. It redraws itself as new filings land.
        </p>
        <div data-testid="landing-theme-curve" className="mt-12 fp-card p-5 sm:p-8 relative">
          <svg viewBox="0 0 200 96" className="w-full h-auto" role="img" aria-label="Live theme conviction curve">
            <path d={`M 6 90 L 6 ${curveY(6)} ${Array.from({ length: 94 }, (_, i) => { const x = 6 + i * 2; return `L ${x} ${curveY(x)}`; }).join(" ")} L 192 90 Z`}
              fill="rgba(255,255,255,0.03)" stroke="none" />
            <path d={`M 6 ${curveY(6)} ${Array.from({ length: 94 }, (_, i) => { const x = 6 + i * 2; return `L ${x} ${curveY(x)}`; }).join(" ")}`}
              fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.7" />
            {[["EMERGING", 39], ["IGNITING", 93], ["DISTRIBUTING", 162]].map(([l, x]) => (
              <text key={l} x={x} y="94" textAnchor="middle" fill="#5A6272" fontSize="3.6" fontFamily="'JetBrains Mono', monospace">{l}</text>
            ))}
            {pts.map((p) => {
              const { t, cx, cy, half } = p;
              const lx = Math.min(Math.max(cx, half + 2), 198 - half);
              const preferUp = cy < 30;
              const pick = (set, needClear = false) =>
                set.find((l) => lx - half > l.end + 3 && (!needClear || cy - l.y > 5.5));
              let lane = preferUp ? pick(upLanes, true) || pick(downLanes) : pick(downLanes) || pick(upLanes, true);
              const up = lane ? upLanes.includes(lane) : false;
              if (lane) lane.end = lx + half;
              const ly = lane?.y;
              const c = COLORS[t.lifecycle] || "#8B93A3";
              return (
                <a key={t.theme_id} href={`/theme/${t.theme_id}`} data-testid={`landing-curve-theme-${t.theme_id}`}>
                  <title>{t.name}</title>
                  {lane && (
                    <>
                      <line x1={cx} y1={up ? cy - (t.gate ? 3.8 : 2.4) : cy + (t.gate ? 3.8 : 2.4)}
                            x2={cx} y2={up ? ly + 2.6 : ly - 4.6}
                            stroke={c} strokeWidth="0.25" opacity="0.4" />
                      {Math.abs(lx - cx) > 1.5 && (
                        <line x1={cx} y1={up ? ly + 2.6 : ly - 4.6}
                              x2={lx} y2={up ? ly + 2.6 : ly - 4.6}
                              stroke={c} strokeWidth="0.25" opacity="0.4" />
                      )}
                    </>
                  )}
                  <circle cx={cx} cy={cy} r="2" fill={c} />
                  {t.gate && <circle cx={cx} cy={cy} r="3.4" fill="none" stroke={c} strokeWidth="0.4" opacity="0.65" />}
                  {lane && (
                    <text x={lx} y={ly} textAnchor="middle" fill="#B7BFCC" fontSize="3.1"
                          fontFamily="'JetBrains Mono', monospace" className="hover:underline cursor-pointer">
                      {t.name}
                    </text>
                  )}
                </a>
              );
            })}
          </svg>
          <span className="absolute bottom-3 right-4 font-mono text-[10px] text-[#3A4152]">
            live{data.as_of ? ` · as of ${data.as_of}` : ""} · fundprints.com
          </span>
        </div>
        <p className="mt-4 font-mono text-[11px] text-[#5A6272] max-w-3xl leading-relaxed">
          Ringed dots are showcase themes, the top of the run by calibrated score. Click any theme
          to see the case. The names inside unlock with a free account.
        </p>
        <a href="/register" data-testid="landing-curve-cta"
           className="inline-block mt-6 font-mono text-[13px] text-[#2BD97C] hover:underline">
          See who's inside each theme →
        </a>
      </div>
    </section>
  );
}
